/**
 * useKillTeamActions - the kill-team's verbs at the table (Squad Mode stage 6): enter, the Cohesion Challenge,
 * leave, start an Oath ability, join one, stop one, and the GM's +/-.
 *
 * Each verb is planned by the engine (`engine/killTeam/acts`) and carried out in one order, everywhere:
 *   1. undo first (`capturePreActionSnapshot`, `pushHistory`), so the press is one step back;
 *   2. the POOL, by compare-and-set against the server's pool (a refusal there - spent meanwhile by another brother -
 *      stops everything, with its sentence);
 *   3. then each brother's Squad Mode, as functions of his latest state;
 *   4. the log's lines.
 * A step that empties the pool drops every brother to Solo in the same pass (`emptiedPlan`).
 *
 * Three verbs belong to something else's undo step and skip step 1: `turnStart` (the End Turn that started it),
 * `drop` (the GM screen's reconcile) and two costs from OUTSIDE a press - heavy fire on a brother (`damage`, behind the `cohesionDamage` house rule)
 * and the (acting) leader's failed Fear test (`fear`, D12). They skip step 1: they belong to the attack that caused
 * them, whose own undo snapshot already holds the pool as it was.
 *
 * Distances come from `tokens` - the RULE tokens (the positions seam): with positions off it is empty, every distance
 * unknown, and an unknown distance counts as in range (D22).
 *
 * @returns the verbs, and `whyNotEnter` for the plates' dimmed reasons; every verb resolves false when nothing happened
 */
import { useMemo, useRef } from 'react';
import { rollD100, rollDie } from '../engine/dice';
import { isBrother, leaderHere } from '../engine/killTeam/record';
import { brotherFacts, distanceIn, type Brother } from '../engine/killTeam/facts';
import { adjust, withPool, type CohesionDoc, type Pool, type Step } from '../engine/killTeam/pool';
import { cannotEnter, supportReach } from '../engine/killTeam/eligibility';
import {
  challengePlan,
  damagePlan,
  emptiedPlan,
  enterPlan,
  fearPlan,
  joinPlan,
  leavePlan,
  startPlan,
  stopPlan,
  strayPlan,
  oathPlan,
  refillPlan,
  turnStartPlan,
  type Act,
  type Brothers,
  type Planned,
} from '../engine/killTeam/acts';
import type { Combatant, CombatState, KillTeamState } from '../engine/roster/types';
import type { KillTeamsState } from './useKillTeams';

import { infoLine } from './shellCombatant';

interface Args {
  roster: Combatant[];
  /** The rule tokens (useShellGround): the markers while positions count, else none. */
  tokens: Record<string, { x: number; z: number }>;
  sceneId: string | null;
  killTeams: KillTeamsState | null;
  isGm: boolean;
  challengeFailure: 'nothing' | 'nextTurn';
  oneSquadAbilityPerTurn: boolean;
  /** The house rule: does heavy fire on a brother cost Cohesion? */
  cohesionDamage: boolean;
  updateCombatantState: (id: string, fn: (st: CombatState) => CombatState) => void;
  addRollNarrative: (entry: unknown) => void;
  capturePreActionSnapshot?: () => void;
  pushHistory?: () => void;
  /** Set once a press has landed: useHistory records its post-state (see `carry`). */
  historyTrack?: { current: boolean };
  pushToast?: (text: string) => void;
}

/** His state with this Squad Mode; Solo is the key ABSENT (gotcha 2). */
function withSquad(st: CombatState, ks: KillTeamState | undefined): CombatState {
  const { killTeam: _was, ...rest } = st;
  return ks ? { ...rest, killTeam: ks } : rest;
}

/** The pool step, the emptied pass and the server's write; the plan to apply after it, or the sentence that stops. */
/** The act's step on this pool (`opens` may make one); null when there is no pool to step. */
function stepOf(act: Act, pool: Pool | undefined): Step | null {
  if (act.opens) return act.opens(pool ?? null);
  return pool ? act.pool!(pool) : null;
}

type Stepped = { doc: CohesionDoc; extra: Act | null; lines: string[] } | { refused: string | null };

/**
 * One try of the pool's write against the server's pools: the act's step, and the emptied pass when THIS step takes
 * the pool to 0 (an act already emptying it, or a cost on an empty pool, would only say it twice - the stage review).
 * `refused` null: there was no pool to step.
 */
function stepPools(doc: CohesionDoc, tag: string, act: Act, roster: readonly Combatant[]): Stepped {
  const pool = doc.pools[tag];
  const step = stepOf(act, pool);
  if (!step) return { refused: null };
  if ('refused' in step) return step;
  const empties = (pool?.pool ?? 0) > 0 && step.next.pool <= 0;
  const extra = empties ? emptiedPlan(step.next, roster, tag, isBrother) : null;
  const next = extra?.pool ? (extra.pool(step.next) as { next: Pool }).next : step.next;
  return { doc: withPool(doc, tag, next), extra, lines: step.lines };
}

/** The pool's write by compare-and-set: what to apply after it, or the sentence that stops everything. */
async function writePool(a: Args, tag: string, act: Act): Promise<{ extra: Act | null; lines: string[] } | string> {
  if (!act.pool && !act.opens) return { extra: null, lines: [] };
  let last: Stepped = { refused: null };
  const written = await a.killTeams!.transact((doc) => {
    last = stepPools(doc, tag, act, a.roster);
    return 'doc' in last ? last.doc : undefined;
  });
  // Set inside the transaction's callback, which TypeScript's narrowing cannot follow.
  const got = last as Stepped;
  if (written && 'doc' in got) return { extra: got.extra, lines: got.lines };
  const refused = 'refused' in got ? got.refused : null;
  return refused ?? 'The pool could not be written: nothing changed. Try again.';
}

/**
 * Carry one plan out, in the header's order; `inline` for what belongs to another step's undo (an attack's cost, a
 * turn start, the GM screen's reconcile), which then takes no undo step of its own.
 */
async function carry(a: Args, tag: string, planned: Planned, inline = false): Promise<boolean> {
  if ('refused' in planned) return refuse(a, planned.refused);
  if (!inline) {
    a.capturePreActionSnapshot?.();
    a.pushHistory?.();
  }
  const pooled = await writePool(a, tag, planned);
  if (typeof pooled === 'string') return refuse(a, pooled);
  for (const s of [...planned.states, ...(pooled.extra?.states ?? [])])
    a.updateCombatantState(s.id, (st) => withSquad(st, s.next(st.killTeam)));
  for (const line of [...planned.lines, ...pooled.lines, ...(pooled.extra?.lines ?? [])])
    a.addRollNarrative(infoLine(line));
  // The post-state is recorded once this lands (useHistory's tracked action), as every undoable press does.
  if (!inline && a.historyTrack) a.historyTrack.current = true;
  return true;
}

function refuse(a: Args, why: string): false {
  a.pushToast?.(why);
  return false;
}

/** The brother's facts, from this render's table: what every verb plans from. */
function brothersOf(a: Args, id: string): Brother | null {
  if (!a.killTeams) return null;
  return brotherFacts(a.roster, a.killTeams.teams, a.killTeams.cohesion?.pools ?? null, id, distanceIn(a.tokens));
}

/** One of his kill-team's Oath abilities, and the library version it was copied at (a running one snapshots it). */
function abilityOf(b: Brothers, abilityId: string) {
  const ability = b.kt.oath?.abilities.find((x) => x.id === abilityId);
  return ability ? { ability, rev: b.kt.rev } : null;
}

/** A verb's plan for one brother; null when there is nothing to do (and nothing to say). */
type Planner<R extends unknown[]> = (a: Args, b: Brother, ...rest: R) => Planned | null;
type TagPlanner<R extends unknown[]> = (a: Args, tag: string, ...rest: R) => Planned | null;

const NOTHING = Promise.resolve(false);

/** A verb on a brother: his record found, his plan made, carried out (`inline`: inside another step's undo). */
function byBrother<R extends unknown[]>(live: { current: Args }, plan: Planner<R>, inline = false) {
  return (id: string, ...rest: R): Promise<boolean> => {
    const a = live.current;
    const b = brothersOf(a, id);
    const planned = b && plan(a, b, ...rest);
    return b && planned ? carry(a, b.tag, planned, inline) : NOTHING;
  };
}

/** A verb on a kill-team's pool. */
function byTag<R extends unknown[]>(live: { current: Args }, plan: TagPlanner<R>, inline = false) {
  return (tag: string, ...rest: R): Promise<boolean> => {
    const a = live.current;
    const planned = plan(a, tag, ...rest);
    return planned ? carry(a, tag, planned, inline) : NOTHING;
  };
}

const runningIn = (a: Args, tag: string, key: string) =>
  a.killTeams?.cohesion?.pools[tag]?.running.find((r) => r.key === key);

/** The kill-team `tag` as the sheets make it, and its pool on this screen (null: none yet). */
function killTeamAt(a: Args, tag: string) {
  const kt = a.killTeams?.teams.killTeams[tag];
  return kt ? { kt, pool: a.killTeams?.cohesion?.pools[tag] ?? null } : null;
}

const fillOne: TagPlanner<[]> = (a, tag) => {
  const at = killTeamAt(a, tag);
  return at ? oathPlan(at.kt, at.pool, a.roster) : null;
};

const refillOne: TagPlanner<[]> = (a, tag) => {
  const at = killTeamAt(a, tag);
  return at?.pool ? refillPlan(at.kt) : null;
};

const startOne: Planner<[string]> = (a, b, abilityId) => {
  const found = abilityOf(b, abilityId);
  if (!found) return { refused: 'The Oath has no such ability.' };
  const at = Date.now();
  const key = `${abilityId}-${at.toString(36)}`;
  const limit = a.oneSquadAbilityPerTurn;
  return startPlan({ brother: b.brother, ...found, sceneId: a.sceneId, key, at, limit, override: a.isGm });
};

const joinOne: Planner<[string]> = (a, b, key) => {
  const running = runningIn(a, b.tag, key);
  if (!running) return { refused: 'That ability is no longer running.' };
  return joinPlan(b.brother, running, a.sceneId, a.oneSquadAbilityPerTurn, a.isGm);
};

/** Heavy fire landed on him (`cohesionCostOf` said 1): only a brother in Squad Mode, only under the house rule. */
const damageOne: Planner<[string]> = (a, b, weapon) =>
  a.cohesionDamage && b.brother.state.killTeam?.on ? damagePlan(b, a.sceneId, weapon, rollD100()) : null;

/** He failed a Fear test against `creature`: the pool pays only when he leads his squad here (D12). */
const fearOne: Planner<[number, string]> = (a, b, fearRating, creature) => {
  const leads =
    leaderHere(
      b.kt,
      a.sceneId,
      b.here.map((c) => c.id),
    ) === b.brother.id;
  return leads && fearRating > 0 ? fearPlan(b.brother.name, fearRating, creature) : null;
};

/** His turn starts (the tracker's callback): range, a waiting entry, the free join and one-shots. */
const turnStartOne: Planner<[]> = (a, b) => {
  const supported = supportReach(b.brother.id, b.here, b.kt.rangeM, b.distance).length > 0;
  return turnStartPlan(b, supported, a.challengeFailure === 'nextTurn');
};

function verbs(live: { current: Args }) {
  return {
    whyNotEnter: (id: string) => {
      const b = brothersOf(live.current, id);
      return b ? cannotEnter(b) : null;
    },
    enter: byBrother(live, (_a, b) => enterPlan(b)),
    challenge: byBrother(live, (a, b) => challengePlan(b, rollDie(10), a.challengeFailure === 'nextTurn')),
    leave: byBrother(live, (a, b) => leavePlan(b.brother, b.pool, a.roster)),
    start: byBrother(live, startOne),
    join: byBrother(live, joinOne),
    damage: byBrother(live, damageOne, true),
    fear: byBrother(live, fearOne, true),
    turnStart: byBrother(live, turnStartOne, true),
    /** Out of Squad Mode for a reason not his own (`why`: "is down: back to Solo Mode"). */
    drop: byBrother(live, (a, b, why: string) => leavePlan(b.brother, b.pool, a.roster, why), true),
    /** No longer a brother (moved to another team, made a subordinate): out of Squad Mode, the pool untouched. */
    stray: (id: string) => {
      const a = live.current;
      const c = a.roster.find((x) => x.id === id);
      return c?.state.killTeam ? carry(a, '', strayPlan(c), true) : NOTHING;
    },
    /**
     * The pool as the kill-team on the sheets wants it (the GM screen's reconcile): filled when the session first finds
     * it without one, reset when the sheets play another Oath. Outside undo, like the sheets that decide it.
     */
    fill: byTag(live, fillOne, true),
    /** The GM's Refill: the pool back to the start the sheets work out now (a new mission, the same session). */
    refill: byTag(live, refillOne),
    stop: byTag(live, (a, tag, key: string) => {
      const running = runningIn(a, tag, key);
      return running ? stopPlan(running, a.roster) : null;
    }),
    /** The pool reads 0 with brothers still in Squad Mode: drop them all (the GM screen's reconcile). */
    empty: byTag(
      live,
      (a, tag) => {
        const pool = a.killTeams?.cohesion?.pools[tag];
        return pool ? emptiedPlan(pool, a.roster, tag, isBrother) : null;
      },
      true,
    ),
    adjust: byTag(live, (_a, _tag, delta: number) => ({ pool: (p) => adjust(p, delta), states: [], lines: [] })),
  };
}

export default function useKillTeamActions(args: Args) {
  const live = useRef(args);
  live.current = args;
  return useMemo(() => verbs(live), []);
}
